import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminCurrentSession } from '@mds/shared/types/admin-auth';

import * as auth from './admin-auth.api';
import { SessionContext, type SessionStatus, type SessionValue } from './session-context';

/**
 * Holds the signed-in administrator for the whole app.
 *
 * The session is an HTTP-only cookie, so the only way to know whether one is
 * live is to ask: `/me` is called once on mount and treated as the source of
 * truth.
 */
export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<AdminCurrentSession | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await auth.getSession();
      setSession(next);
      setStatus('authenticated');
    } catch (error) {
      // 401 is the ordinary signed-out answer, not a failure worth surfacing.
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('Could not load the admin session', error);
      }
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      // The endpoint is idempotent and clears the cookie either way, so the
      // local state goes regardless of what the network did.
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<SessionValue>(() => {
    const permissions = new Set(session?.permissions.map((p) => p.key) ?? []);
    const roles = new Set(session?.roles.map((r) => r.key) ?? []);
    return {
      status,
      session,
      refresh,
      signOut,
      can: (permission) => permissions.has(permission),
      is: (role) => roles.has(role),
    };
  }, [status, session, refresh, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
